import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface Verificacoes {
  temDescricao: boolean;
  temImagens: boolean;
  temLocalizacao: boolean;
  temGeneros: boolean;
  temMusicas: boolean;
  temAlbuns: boolean;
  temVideos: boolean;
}

interface BandaIncompleta {
  id_banda: number;
  nome_banda: string;
  nome_responsavel: string;
  email: string;
  phone: string;
  verificacoes: Verificacoes;
  created_at: string; // Nova propriedade para a data de criação
}

const AttPerfil: React.FC = () => {
  const [bandas, setBandas] = useState<BandaIncompleta[]>([]);
  const [expandedType, setExpandedType] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/bandas-incompletas');
      
      // Adicionar console.log para depurar
      console.log('Dados recebidos do backend:', response.data);
      
      // Verificar se cada banda tem o campo created_at
      const bandasComData = response.data.map((banda: any) => {
        if (!banda.created_at) {
          console.log(`Banda ${banda.id_banda} (${banda.nome_banda}) não tem data de criação`);
        }
        return banda;
      });
      
      // Ordenar as bandas por data de criação (das mais antigas para as mais recentes)
      const bandasOrdenadas = bandasComData.sort((a: BandaIncompleta, b: BandaIncompleta) => {
        // Verificar se ambas as datas existem
        if (a.created_at && b.created_at) {
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        }
        // Se a data não existir, mover para o final
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return 0;
      });
      
      setBandas(bandasOrdenadas);
    } catch (err) {
      setError('Erro ao carregar os dados.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleType = (type: string) => {
    setExpandedType(expandedType === type ? null : type);
  };

  const getStatusIcon = (status: boolean) => {
    return status ? "✅" : "❌";
  };

  const formatarData = (dataString: string) => {
    if (!dataString) return 'Data não disponível';
    
    console.log(`Tentando formatar data: "${dataString}"`);
    
    try {
      const data = new Date(dataString);
      
      // Verificar se a data é válida
      if (isNaN(data.getTime())) {
        console.log(`Data inválida: "${dataString}"`);
        return 'Data inválida';
      }
      
      return data.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error(`Erro ao processar data "${dataString}":`, error);
      return 'Erro ao processar data';
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copiado para a área de transferência!');
  };

  const marcarMensagemEnviada = async (idBanda: number) => {
    try {
      await api.put(`/admin/bandas/${idBanda}/mensagem-enviada`);
      // Atualiza a lista removendo a banda que foi marcada
      setBandas(bandas.filter(banda => banda.id_banda !== idBanda));
    } catch (err) {
      console.error('Erro ao atualizar status da mensagem:', err);
      alert('Erro ao atualizar status da mensagem. Tente novamente.');
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Perfis Incompletos</h1>
      {loading && <p style={styles.message}>Carregando...</p>}
      {error && <p style={{ ...styles.message, color: '#dc3545' }}>{error}</p>}

      {!loading && (
        <div>
          <div style={styles.section}>
            <button
              style={{
                ...styles.typeButton,
                backgroundColor: expandedType === 'bandas' ? '#ff6600' : '#f2f2f2',
                color: expandedType === 'bandas' ? 'white' : '#333',
              }}
              onClick={() => toggleType('bandas')}
            >
              Bandas com Perfil Incompleto ({bandas.length})
            </button>
            {expandedType === 'bandas' && (
              <div style={styles.profileList}>
                {bandas.map((banda) => (
                  <div key={banda.id_banda} style={styles.profileCard}>
                    <div style={styles.cardHeader}>
                      <h3 style={styles.profileName}>{banda.nome_banda}</h3>
                      <div style={styles.dateInfo}>
                        📅 Cadastrada em: {formatarData(banda.created_at)}
                        {!banda.created_at && <span style={{color: 'red'}}> (Verifique se o backend está enviando o campo created_at)</span>}
                      </div>
                      <div style={styles.contactInfo}>
                        <span 
                          style={styles.copyable}
                          onClick={() => copyToClipboard(banda.nome_responsavel)}
                          title="Clique para copiar"
                        >
                          👤 {banda.nome_responsavel || 'Sem responsável'}
                        </span>
                        <span 
                          style={styles.copyable}
                          onClick={() => copyToClipboard(banda.email)}
                          title="Clique para copiar"
                        >
                          📧 {banda.email}
                        </span>
                        <span 
                          style={styles.copyable}
                          onClick={() => copyToClipboard(banda.phone)}
                          title="Clique para copiar"
                        >
                          📱 {banda.phone}
                        </span>
                      </div>
                    </div>
                    <div style={styles.verificacoesList}>
                      <div style={styles.verificacaoItem}>
                        {getStatusIcon(banda.verificacoes.temDescricao)} Descrição
                      </div>
                      <div style={styles.verificacaoItem}>
                        {getStatusIcon(banda.verificacoes.temImagens)} Imagens de Perfil
                      </div>
                      <div style={styles.verificacaoItem}>
                        {getStatusIcon(banda.verificacoes.temLocalizacao)} Localização
                      </div>
                      <div style={styles.verificacaoItem}>
                        {getStatusIcon(banda.verificacoes.temGeneros)} Gêneros
                      </div>
                      <div style={styles.verificacaoItem}>
                        {getStatusIcon(banda.verificacoes.temMusicas)} Músicas
                      </div>
                      <div style={styles.verificacaoItem}>
                        {getStatusIcon(banda.verificacoes.temAlbuns)} Álbum de Fotos
                      </div>
                      <div style={styles.verificacaoItem}>
                        {getStatusIcon(banda.verificacoes.temVideos)} Vídeos
                      </div>
                    </div>
                    <div style={styles.cardFooter}>
                      <button
                        style={styles.mensagemButton}
                        onClick={() => marcarMensagemEnviada(banda.id_banda)}
                      >
                        ✔️ Marcar Mensagem como Enviada
                      </button>
                    </div>
                  </div>
                ))}
                {bandas.length === 0 && (
                  <p style={styles.message}>Nenhuma banda com perfil incompleto.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1000px',
    margin: '0 auto',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  title: {
    fontSize: '24px',
    color: '#333',
    textAlign: 'center' as 'center',
    marginBottom: '20px',
  },
  message: {
    fontSize: '16px',
    textAlign: 'center' as 'center',
    color: '#555',
  },
  section: {
    marginBottom: '20px',
  },
  typeButton: {
    width: '100%',
    padding: '10px',
    textAlign: 'left' as 'left',
    fontSize: '18px',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginBottom: '10px',
    transition: 'background-color 0.3s ease',
  },
  profileList: {
    paddingLeft: '10px',
  },
  profileCard: {
    padding: '15px',
    marginBottom: '15px',
    backgroundColor: 'white',
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  cardHeader: {
    marginBottom: '15px',
  },
  profileName: {
    fontSize: '18px',
    color: '#333',
    margin: 0,
    marginBottom: '10px',
    fontWeight: 'bold',
  },
  dateInfo: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '10px',
  },
  contactInfo: {
    display: 'flex',
    gap: '20px',
    fontSize: '14px',
  },
  verificacoesList: {
    display: 'flex',
    flexWrap: 'wrap' as 'wrap',
    gap: '10px',
  },
  verificacaoItem: {
    backgroundColor: '#f5f5f5',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  copyable: {
    cursor: 'pointer',
    color: '#0066cc',
    textDecoration: 'underline',
    ':hover': {
      color: '#0052a3',
    },
  },
  cardFooter: {
    marginTop: '15px',
    paddingTop: '15px',
    borderTop: '1px solid #eee',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  mensagemButton: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#218838',
    },
  },
};

export default AttPerfil;